import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BadgeEuro,
  ClipboardList,
  Clock,
  FileText,
  Trash2,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { devisQuoteTabsScrollClass } from './AdminChrome';
import DevisQuoteSalairesTab from './DevisQuoteSalairesTab';
import DevisQuoteFraisGenerauxTab from './DevisQuoteFraisGenerauxTab';
import DevisQuoteMainOeuvreTab from './DevisQuoteMainOeuvreTab';
import DevisQuoteInvestissementTab from './DevisQuoteInvestissementTab';
import DevisQuoteResumeTab from './DevisQuoteResumeTab';
import {
  defaultDevisUiState,
  defaultFraisGenerauxState,
  defaultInvestissementState,
  defaultMainOeuvreState,
  ensureStaffLinesForMainOeuvre,
  mergeDevisUiState,
  normalizeOperationCategories,
  todayYmdLocal,
} from './devisQuoteState';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const SAVE_DEBOUNCE_MS = 400;

function DevisQuoteTabTrigger({ value, active, theme, icon: Icon, children }) {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        'flex shrink-0 snap-center items-center justify-center gap-2 rounded-xl border-2 px-3.5 py-2.5 text-left text-xs font-semibold transition-all duration-200 sm:px-4 sm:text-sm',
        'min-h-[48px] min-w-[max-content] max-w-[220px] touch-manipulation sm:min-h-[44px] sm:max-w-none',
        active
          ? 'shadow-md'
          : 'border-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
      )}
      style={
        active
          ? {
              backgroundColor: theme.surface,
              color: theme.accent,
              borderColor: theme.accent,
            }
          : {
              backgroundColor: 'transparent',
              color: theme.textSecondary,
            }
      }
    >
      <Icon className="h-4 w-4 shrink-0 opacity-90" strokeWidth={2} aria-hidden />
      <span className="leading-tight">{children}</span>
    </TabsTrigger>
  );
}

const DevisQuoteEditor = ({
  quoteId,
  token,
  apiBase,
  theme,
  canEditQuotes = true,
  onUnauthorized,
  onBack,
  onDeleted,
  onQuoteMetadataChange,
}) => {
  const [title, setTitle] = useState('');
  const [quoteDate, setQuoteDate] = useState(() => todayYmdLocal());
  const [clientCompany, setClientCompany] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('draft');
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  const [devisUiState, setDevisUiState] = useState(() => defaultDevisUiState());
  const [qualifications, setQualifications] = useState([]);
  const [operationCategories, setOperationCategories] = useState([]);
  const operationCategoriesRef = useRef(operationCategories);
  const qualificationsRef = useRef(qualifications);
  operationCategoriesRef.current = operationCategories;
  qualificationsRef.current = qualifications;
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle');
  const hydrated = useRef(false);
  const devisUiStateRef = useRef(devisUiState);
  devisUiStateRef.current = devisUiState;
  const titleRef = useRef(title);
  titleRef.current = title;
  const quoteDateRef = useRef(quoteDate);
  quoteDateRef.current = quoteDate;
  const clientCompanyRef = useRef(clientCompany);
  clientCompanyRef.current = clientCompany;
  const quoteStatusRef = useRef(quoteStatus);
  quoteStatusRef.current = quoteStatus;

  const readOnly = !canEditQuotes || quoteStatus === 'finalized';

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Une erreur est survenue';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadAll = useCallback(async () => {
    if (!quoteId || !token) return;
    hydrated.current = false;
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [qRes, qualRes, opCatRes] = await Promise.all([
        axios.get(`${apiBase}/quotes/${quoteId}`, { headers }),
        axios.get(`${apiBase}/qualifications`, { headers }),
        axios.get(`${apiBase}/operation-categories`, { headers }),
      ]);
      setTitle(qRes.data?.title || '');
      setQuoteDate(qRes.data?.quote_date || todayYmdLocal());
      setClientCompany(qRes.data?.client_company ?? '');
      setQuoteStatus(qRes.data?.status === 'finalized' ? 'finalized' : 'draft');
      setDevisUiState(mergeDevisUiState(qRes.data?.devis_ui_state));
      setQualifications(Array.isArray(qualRes.data) ? qualRes.data : []);
      setOperationCategories(normalizeOperationCategories(opCatRes.data));
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
      hydrated.current = true;
    }
  }, [apiBase, quoteId, token, handleApiError]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const persistQuoteStatus = useCallback(
    async (nextStatus) => {
      if (!quoteId || !token || !canEditQuotes) return;
      setStatusActionLoading(true);
      try {
        await axios.patch(
          `${apiBase}/quotes/${quoteId}`,
          { status: nextStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setQuoteStatus(nextStatus);
        onQuoteMetadataChange?.();
        toast.success(
          nextStatus === 'finalized' ? 'Devis finalisé.' : 'Devis repassé en brouillon — vous pouvez à nouveau le modifier.'
        );
      } catch (e) {
        handleApiError(e);
      } finally {
        setStatusActionLoading(false);
      }
    },
    [apiBase, quoteId, token, canEditQuotes, onQuoteMetadataChange, handleApiError]
  );

  useEffect(() => {
    if (loading || !quoteId || readOnly) return;
    setDevisUiState((prev) => {
      const next = ensureStaffLinesForMainOeuvre(prev, operationCategories, qualifications);
      return next || prev;
    });
  }, [loading, quoteId, readOnly, devisUiState.main_oeuvre, operationCategories, qualifications]);

  useEffect(() => {
    if (!hydrated.current || !quoteId || !token || readOnly) return undefined;
    const id = setTimeout(async () => {
      setSaveState('saving');
      try {
        await axios.patch(
          `${apiBase}/quotes/${quoteId}`,
          {
            title: titleRef.current.trim() || 'Nouveau devis',
            quote_date: quoteDateRef.current,
            client_company: clientCompanyRef.current,
            devis_ui_state: devisUiStateRef.current,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaveState('saved');
        setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1600);
      } catch (e) {
        setSaveState('error');
        handleApiError(e);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [devisUiState, title, quoteDate, clientCompany, quoteId, token, apiBase, handleApiError, readOnly]);

  const patchSalaires = (fn) => {
    if (!canEditQuotes || quoteStatusRef.current === 'finalized') return;
    setDevisUiState((prev) => ({
      ...prev,
      salaires: fn(prev.salaires),
    }));
  };

  const patchFraisGeneraux = (fn) => {
    if (!canEditQuotes || quoteStatusRef.current === 'finalized') return;
    setDevisUiState((prev) => {
      const base =
        prev.frais_generaux &&
        typeof prev.frais_generaux === 'object' &&
        prev.frais_generaux.linesByCategory
          ? prev.frais_generaux
          : defaultFraisGenerauxState();
      return {
        ...prev,
        frais_generaux: fn(base),
      };
    });
  };

  const patchMainOeuvre = (fn) => {
    if (!canEditQuotes || quoteStatusRef.current === 'finalized') return;
    setDevisUiState((prev) => {
      const base =
        prev.main_oeuvre &&
        typeof prev.main_oeuvre === 'object' &&
        prev.main_oeuvre.inputsByOperationId
          ? prev.main_oeuvre
          : defaultMainOeuvreState();
      const nextMo = fn(base);
      const merged = { ...prev, main_oeuvre: nextMo };
      const ensured = ensureStaffLinesForMainOeuvre(
        merged,
        operationCategoriesRef.current,
        qualificationsRef.current
      );
      return ensured || merged;
    });
  };

  const patchInvestissement = (fn) => {
    if (!canEditQuotes || quoteStatusRef.current === 'finalized') return;
    setDevisUiState((prev) => {
      const inv = prev.investissement && typeof prev.investissement === 'object' ? prev.investissement : {};
      const base = {
        ...defaultInvestissementState(),
        ...inv,
        linesByCategory:
          inv.linesByCategory && typeof inv.linesByCategory === 'object' ? inv.linesByCategory : {},
      };
      return {
        ...prev,
        investissement: fn(base),
      };
    });
  };

  const [devisTab, setDevisTab] = useState('salaires');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const confirmDeleteQuote = async () => {
    if (!quoteId || !token) return;
    setDeleting(true);
    try {
      await axios.delete(`${apiBase}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Devis supprimé.');
      setDeleteOpen(false);
      onDeleted?.();
    } catch (e) {
      handleApiError(e);
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  if (loading) {
    return (
      <div className="w-full min-w-0 space-y-4" role="status" aria-live="polite">
        <span className="sr-only">Chargement du devis…</span>
        <div className="animate-pulse space-y-4">
          <div className="h-16 rounded-2xl" style={{ backgroundColor: theme.panelInset }} />
          <div className="h-44 max-w-2xl rounded-2xl" style={{ backgroundColor: theme.panelInset }} />
          <div className="flex gap-2 overflow-hidden pt-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-12 w-[7.5rem] shrink-0 rounded-xl"
                style={{ backgroundColor: theme.panelInset }}
              />
            ))}
          </div>
          <div className="h-32 rounded-2xl" style={{ backgroundColor: theme.panelInset }} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-4 sm:space-y-6">
      <div
        className="relative overflow-hidden rounded-2xl border shadow-sm"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
      >
        <div
          className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
          style={{
            background: `linear-gradient(90deg, ${theme.accent} 0%, ${theme.accentGlow} 50%, ${theme.accent} 100%)`,
          }}
        />
        <div className="relative space-y-4 p-4 sm:p-5">
          {readOnly && (
            <div
              className="rounded-xl border-2 px-4 py-3 text-sm leading-relaxed"
              style={
                !canEditQuotes
                  ? { borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.text }
                  : {
                      borderColor: 'rgba(22, 163, 74, 0.45)',
                      backgroundColor: 'rgba(22, 163, 74, 0.1)',
                      color: theme.text,
                    }
              }
            >
              {!canEditQuotes ? (
                <>
                  <span className="font-semibold" style={{ color: theme.text }}>
                    Consultation seule
                  </span>
                  {' — '}
                  vous n’avez pas la permission de créer ou de modifier des devis. Vous pouvez parcourir les onglets et
                  consulter le résumé.
                </>
              ) : (
                <>
                  <span className="font-semibold text-emerald-800 dark:text-emerald-200">Devis finalisé</span>
                  {' — '}
                  consultation seule. Utilisez l’onglet Résumé pour le PDF ou pour repasser en brouillon.
                </>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <Button
              type="button"
              className="h-12 w-full gap-2.5 px-5 text-base font-semibold shadow-md transition-[filter,transform] hover:brightness-110 active:scale-[0.99] sm:h-11 sm:w-auto touch-manipulation"
              style={{
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: theme.accent,
                backgroundColor: theme.accent,
                color: '#fff',
              }}
              onClick={onBack}
            >
              <ArrowLeft className="h-5 w-5 shrink-0" strokeWidth={2.5} aria-hidden />
              Retour à la liste
            </Button>
            <div className="flex flex-wrap items-center justify-stretch gap-2 sm:justify-end sm:gap-3">
              {saveState !== 'idle' && (
                <span
                  className={cn(
                    'inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 py-2 text-xs font-medium tabular-nums sm:flex-initial sm:justify-start',
                    saveState === 'error' && 'text-destructive'
                  )}
                  style={{
                    backgroundColor: saveState === 'error' ? 'rgba(239,68,68,0.1)' : theme.accentSoft,
                    color: saveState === 'error' ? undefined : theme.textSecondary,
                  }}
                >
                  {saveState === 'saving' && 'Enregistrement…'}
                  {saveState === 'saved' && 'Enregistré'}
                  {saveState === 'error' && 'Erreur d’enregistrement'}
                </span>
              )}
              {canEditQuotes && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 min-h-[44px] flex-1 gap-2 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive sm:h-10 sm:flex-initial touch-manipulation"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                  Supprimer
                </Button>
              )}
            </div>
          </div>

          <div className="grid max-w-2xl gap-2.5 sm:grid-cols-2 sm:gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="quote-title" className="text-[11px] font-medium leading-none" style={{ color: theme.textSecondary }}>
                Nom du devis
              </Label>
              <Input
                id="quote-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                className="h-8 rounded-md border px-2.5 text-sm touch-manipulation sm:h-9"
                style={inputStyle}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="quote-date" className="text-[11px] font-medium leading-none" style={{ color: theme.textSecondary }}>
                Date du devis
              </Label>
              <Input
                id="quote-date"
                type="date"
                value={quoteDate}
                onChange={(e) => setQuoteDate(e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                className="h-8 rounded-md border px-2.5 text-sm touch-manipulation sm:h-9"
                style={inputStyle}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="quote-company" className="text-[11px] font-medium leading-none" style={{ color: theme.textSecondary }}>
                Entreprise
              </Label>
              <Input
                id="quote-company"
                value={clientCompany}
                onChange={(e) => setClientCompany(e.target.value)}
                placeholder="Client ou site concerné"
                readOnly={readOnly}
                disabled={readOnly}
                className="h-8 min-w-0 rounded-md border px-2.5 text-sm touch-manipulation sm:h-9"
                style={inputStyle}
              />
            </div>
          </div>
        </div>
      </div>

      <Tabs value={devisTab} onValueChange={setDevisTab} className="w-full min-w-0">
        <div className={devisQuoteTabsScrollClass}>
          <TabsList
            className={cn(
              'inline-flex h-auto w-max min-w-full flex-nowrap items-stretch justify-start gap-2 rounded-2xl border-2 p-2 shadow-sm',
              'sm:flex sm:w-full sm:flex-wrap sm:justify-center sm:gap-2'
            )}
            style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
          >
            <DevisQuoteTabTrigger value="salaires" active={devisTab === 'salaires'} theme={theme} icon={BadgeEuro}>
              Salaires
            </DevisQuoteTabTrigger>
            <DevisQuoteTabTrigger
              value="frais_generaux"
              active={devisTab === 'frais_generaux'}
              theme={theme}
              icon={Wallet}
            >
              Frais généraux
            </DevisQuoteTabTrigger>
            <DevisQuoteTabTrigger
              value="main_oeuvre"
              active={devisTab === 'main_oeuvre'}
              theme={theme}
              icon={Clock}
            >
              Main d’œuvre
            </DevisQuoteTabTrigger>
            <DevisQuoteTabTrigger
              value="investissement"
              active={devisTab === 'investissement'}
              theme={theme}
              icon={ClipboardList}
            >
              Investissement
            </DevisQuoteTabTrigger>
            <DevisQuoteTabTrigger
              value="chiffrage_global"
              active={devisTab === 'chiffrage_global'}
              theme={theme}
              icon={FileText}
            >
              Résumé
            </DevisQuoteTabTrigger>
          </TabsList>
        </div>

        <TabsContent value="salaires" className="mt-4 min-w-0 outline-none sm:mt-6 focus-visible:outline-none">
          <DevisQuoteSalairesTab
            salaires={devisUiState.salaires}
            qualifications={qualifications}
            mainOeuvre={devisUiState.main_oeuvre}
            operationCategories={operationCategories}
            theme={theme}
            patchSalaires={patchSalaires}
            canEdit={!readOnly}
          />
        </TabsContent>

        <TabsContent value="frais_generaux" className="mt-4 min-w-0 outline-none sm:mt-6 focus-visible:outline-none">
          <DevisQuoteFraisGenerauxTab
            fraisGeneraux={devisUiState.frais_generaux}
            theme={theme}
            patchFraisGeneraux={patchFraisGeneraux}
            canEdit={!readOnly}
          />
        </TabsContent>

        <TabsContent value="main_oeuvre" className="mt-4 min-w-0 outline-none sm:mt-6 focus-visible:outline-none">
          <DevisQuoteMainOeuvreTab
            operationCategories={operationCategories}
            mainOeuvre={devisUiState.main_oeuvre}
            theme={theme}
            patchMainOeuvre={patchMainOeuvre}
            canEdit={!readOnly}
          />
        </TabsContent>

        <TabsContent value="investissement" className="mt-4 min-w-0 outline-none sm:mt-6 focus-visible:outline-none">
          <DevisQuoteInvestissementTab
            investissement={devisUiState.investissement}
            operationCategories={operationCategories}
            theme={theme}
            patchInvestissement={patchInvestissement}
            canEdit={!readOnly}
          />
        </TabsContent>

        <TabsContent value="chiffrage_global" className="mt-4 min-w-0 outline-none sm:mt-6 focus-visible:outline-none">
          <DevisQuoteResumeTab
            quoteId={quoteId}
            quoteTitle={title}
            quoteDate={quoteDate}
            clientCompany={clientCompany}
            token={token}
            apiBase={apiBase}
            salaires={devisUiState.salaires}
            qualifications={qualifications}
            mainOeuvre={devisUiState.main_oeuvre}
            operationCategories={operationCategories}
            fraisGeneraux={devisUiState.frais_generaux}
            investissement={devisUiState.investissement}
            theme={theme}
            quoteFinalized={quoteStatus === 'finalized'}
            canChangeStatus={canEditQuotes}
            statusActionLoading={statusActionLoading}
            onFinalizeQuote={() => persistQuoteStatus('finalized')}
            onReopenDraft={() => persistQuoteStatus('draft')}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: theme.text }}>Supprimer ce devis ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: theme.textSecondary }}>
              « {title.trim() || 'Sans titre'} » sera définitivement supprimé. Cette action est
              irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="mt-2 border-2 shadow-sm hover:brightness-110 dark:hover:bg-white/10 sm:mt-0"
              style={{
                borderColor: theme.border,
                backgroundColor: theme.panelInset,
                color: theme.text,
              }}
            >
              Annuler
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => confirmDeleteQuote()}
            >
              {deleting ? 'Suppression…' : 'Supprimer'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevisQuoteEditor;
